import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Integritetspolicy · Aminah Ådin";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#2C2825]" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-6 py-20 md:py-28">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#8C7362] hover:text-[#2C2825] transition-colors mb-10"
        >
          <ArrowLeft className="h-4 w-4" />
          Tillbaka
        </Link>

        <p className="text-xs tracking-[0.3em] uppercase text-[#8C7362] mb-5">
          Integritetspolicy
        </p>
        <h1 className="font-serif-display text-4xl md:text-5xl text-[#2C2825] leading-tight">
          Integritetspolicy
        </h1>
        <p className="text-sm text-[#8C7362] mt-4 italic">
          Senast uppdaterad: 2025-10-15
        </p>

        <div className="mt-12 space-y-8 text-[#5C5651] leading-relaxed text-base md:text-lg">
          <p>
            Jag värnar om din personliga integritet och vill att du ska känna
            dig trygg när du lämnar dina personuppgifter. Den här
            integritetspolicyn beskriver hur jag samlar in, använder och skyddar
            dina uppgifter när du besöker min webbplats eller kontaktar mig.
          </p>

          <Section title="1. Vem ansvarar för dina personuppgifter">
            <p>
              <strong className="text-[#2C2825]">Aminah Ådin Empowerment AB</strong>
              <br />
              Organisationsnummer: 559442-5141
            </p>
            <p>
              Aminah Ådin Empowerment AB är personuppgiftsansvarig och ansvarar
              för att dina uppgifter hanteras i enlighet med gällande
              lagstiftning (GDPR).
            </p>
          </Section>

          <Section title="2. Vilka uppgifter som samlas in">
            <p>Jag samlar endast in personuppgifter som du själv lämnar, till exempel när du:</p>
            <List
              items={[
                "fyller i ett kontaktformulär",
                "anmäler dig till nyhetsbrev",
                "bokar ett samtal, köper en tjänst eller laddar ner material",
              ]}
            />
            <p>
              De uppgifter som kan samlas in är namn, e-postadress,
              telefonnummer samt eventuellt andra uppgifter du frivilligt anger.
            </p>
          </Section>

          <Section title="3. Syftet med insamlingen">
            <p>Dina uppgifter används för att:</p>
            <List
              items={[
                "svara på förfrågningar och hantera kontakt",
                "skicka information, nyhetsbrev och erbjudanden (om du samtyckt till det)",
                "hantera bokningar, köp och fakturering",
                "förbättra webbplatsen och kundupplevelsen",
              ]}
            />
          </Section>

          <Section title="4. Lagring och säkerhet">
            <p>
              Dina personuppgifter lagras säkert och endast så länge det behövs
              för att uppfylla syftet ovan eller enligt lagkrav. Jag delar inte
              dina uppgifter med obehöriga. Vid behov används betrodda
              leverantörer som uppfyller GDPR, till exempel för nyhetsbrev,
              bokningssystem eller betalningslösningar.
            </p>
          </Section>

          <Section title="5. Delning till tredje part">
            <p>
              För att kunna analysera och förbättra min verksamhet kan jag
              använda tredjepartsverktyg, exempelvis:
            </p>
            <List items={["Meta (Facebook/Instagram) Pixel för marknadsföring"]} />
            <p>
              Dessa verktyg kan använda cookies för att samla in anonymiserad
              information. Du kan när som helst välja att stänga av cookies i
              din webbläsare.
            </p>
          </Section>

          <Section title="6. Dina rättigheter">
            <p>Du har rätt att:</p>
            <List
              items={[
                "få veta vilka uppgifter jag har om dig",
                "begära rättelse eller radering av dina uppgifter",
                "invända mot eller begränsa hur dina uppgifter används",
                "återkalla ditt samtycke till nyhetsbrev eller marknadsföring när som helst",
              ]}
            />
            <p>
              För att utöva dina rättigheter, kontakta mig via e-post:{" "}
              <a
                href="mailto:aminah@aminahadin.com"
                className="text-[#A98C78] underline underline-offset-4 hover:text-[#8C7362]"
              >
                aminah@aminahadin.com
              </a>
            </p>
          </Section>

          <Section title="7. Ändringar av policyn">
            <p>
              Jag kan uppdatera denna integritetspolicy vid behov. Den senaste
              versionen finns alltid tillgänglig på denna sida.
            </p>
          </Section>
        </div>

        <div className="mt-16 pt-8 border-t border-[#E6DFD7]">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#8C7362] hover:text-[#2C2825] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Tillbaka till startsidan
          </Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-3">
      <h2 className="font-serif-display text-2xl md:text-3xl text-[#2C2825] leading-tight">
        {title}
      </h2>
      {children}
    </section>
  );
}

function List({ items }) {
  return (
    <ul className="space-y-2 pl-1">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className="mt-3 h-1.5 w-1.5 rounded-full bg-[#A98C78] flex-shrink-0" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}
